"use client"

import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { analyzeCopy, type ScanResult } from "@/lib/proto/translation-scan"
import { diagnostic } from "@/lib/proto/content"
import { cn } from "@/lib/utils"

// The positioning scan — the live AI diagnostic (F1). (v4: Translation lexicon
// retired sitewide → 折價/溢價; engine file name translation-scan.ts kept —
// scan logic is a red line, untouched.)
// Runs deterministic heuristics over the visitor's own copy so the result is
// real and specific. The staged reveal makes computation legible as theater.

const SAMPLE =
  "We are a leading provider of innovative, end-to-end solutions that leverage cutting-edge technology to deliver robust, scalable storage and edge-AI platforms. Our world-class, best-in-class ecosystem empowers customers to optimize performance and unlock next-generation value."

// Interface microcopy zh-ified per v3 delta H9 (裁定 D4, 2026-07-11) — the
// scan engine (translation-scan.ts) is untouched; comparative scoring stays
// in its own lane.
const STAGES = diagnostic.stages

type Phase = "idle" | "scanning" | "result"

type State = { phase: Phase; stage: number; result: ScanResult | null }

type Action =
  | { type: "start" }
  | { type: "advance" }
  | { type: "done"; result: ScanResult }
  | { type: "reset" }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "start":
      return { phase: "scanning", stage: 0, result: null }
    case "advance":
      return { ...state, stage: Math.min(state.stage + 1, STAGES.length - 1) }
    case "done":
      return { phase: "result", stage: STAGES.length, result: action.result }
    case "reset":
      return { phase: "idle", stage: 0, result: null }
  }
}

export function DiagnosticInstrument({
  accent = "#C9A227",
  className,
}: {
  accent?: string
  className?: string
}) {
  const [text, setText] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [state, dispatch] = useReducer(reducer, {
    phase: "idle",
    stage: 0,
    result: null,
  })
  const reduce = useReducedMotion()
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }, [])

  useEffect(() => clearTimers, [clearTimers])

  const run = useCallback(() => {
    const result = analyzeCopy(text)
    if (!result) {
      setError(diagnostic.error)
      return
    }
    setError(null)
    clearTimers()

    if (reduce) {
      dispatch({ type: "done", result })
      return
    }

    dispatch({ type: "start" })
    const step = 520
    for (let i = 1; i < STAGES.length; i++) {
      timers.current.push(setTimeout(() => dispatch({ type: "advance" }), step * i))
    }
    timers.current.push(
      setTimeout(() => dispatch({ type: "done", result }), step * STAGES.length),
    )
  }, [text, reduce, clearTimers])

  const reset = useCallback(() => {
    clearTimers()
    dispatch({ type: "reset" })
  }, [clearTimers])

  return (
    <div
      className={cn(
        "w-full rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm",
        "shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_30px_80px_-40px_rgba(0,0,0,0.8)]",
        className,
      )}
      style={{ ["--accent" as string]: accent }}
    >
      <header className="flex items-center justify-between gap-4 border-b border-white/10 px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <span
            className="size-2 rounded-full"
            style={{ backgroundColor: accent, boxShadow: `0 0 12px ${accent}` }}
          />
          <span
            className="text-[0.7rem] uppercase tracking-[0.22em] text-white/55"
            style={{ fontFamily: "var(--proto-mono, ui-monospace, monospace)" }}
          >
            {diagnostic.title}
          </span>
        </div>
        {state.phase === "result" && (
          <button
            onClick={reset}
            className="text-[0.7rem] uppercase tracking-[0.18em] text-white/55 transition-colors hover:text-white/80"
            style={{ fontFamily: "var(--proto-mono, ui-monospace, monospace)" }}
          >
            {diagnostic.rerun}
          </button>
        )}
      </header>

      <div className="p-5 sm:p-6">
        <AnimatePresence mode="wait">
          {state.phase === "idle" && (
            <motion.div
              key="input"
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={reduce ? undefined : { opacity: 0 }}
            >
              <p className="mb-4 max-w-prose text-[0.95rem] leading-relaxed text-white/65">
                {diagnostic.intro}
              </p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={diagnostic.placeholder}
                rows={4}
                spellCheck={false}
                className={cn(
                  "w-full resize-none rounded-xl border border-white/10 bg-black/30 p-4",
                  "text-[0.95rem] leading-relaxed text-white/90 placeholder:text-white/30",
                  "outline-none transition-colors focus:border-[var(--accent)]",
                )}
              />
              {error && (
                <p className="mt-2 text-sm" style={{ color: accent }}>
                  {error}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  onClick={run}
                  className={cn(
                    "group inline-flex items-center gap-2 rounded-full px-5 py-2.5",
                    "text-sm font-medium text-black transition-transform hover:scale-[1.02] active:scale-95",
                  )}
                  style={{ backgroundColor: accent }}
                >
                  {diagnostic.cta}
                  <span aria-hidden className="transition-transform group-hover:translate-x-0.5">
                    →
                  </span>
                </button>
                <button
                  onClick={() => setText(SAMPLE)}
                  className="text-sm text-white/55 underline-offset-4 transition-colors hover:text-white/70 hover:underline"
                >
                  {diagnostic.sampleLink}
                </button>
              </div>
            </motion.div>
          )}

          {state.phase === "scanning" && (
            <motion.div
              key="scan"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-6"
              style={{ fontFamily: "var(--proto-mono, ui-monospace, monospace)" }}
            >
              {STAGES.map((label, i) => {
                const done = i < state.stage
                const active = i === state.stage
                return (
                  <div
                    key={label}
                    className={cn(
                      "flex items-center gap-3 py-1.5 text-sm transition-opacity",
                      i > state.stage && "opacity-30",
                    )}
                  >
                    <span
                      className="grid size-4 place-items-center text-[0.7rem]"
                      style={{ color: done || active ? accent : "rgba(255,255,255,0.3)" }}
                    >
                      {done ? "✓" : active ? <Spinner /> : "·"}
                    </span>
                    <span className={cn(done ? "text-white/55" : active ? "text-white/90" : "text-white/55")}>
                      {label}
                      {active && <Dots />}
                    </span>
                  </div>
                )
              })}
            </motion.div>
          )}

          {state.phase === "result" && state.result && (
            <ResultView key="result" result={state.result} accent={accent} reduce={!!reduce} />
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function ResultView({
  result,
  accent,
  reduce,
}: {
  result: ScanResult
  accent: string
  reduce: boolean
}) {
  const stagger = (i: number) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: { delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
        }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <motion.div {...stagger(0)} className="flex items-end justify-between gap-6">
        <div>
          <ScoreLabel>Clarity score</ScoreLabel>
          <div className="flex items-baseline gap-2">
            <CountUp to={result.clarityScore} reduce={reduce} accent={accent} />
            <span className="text-2xl text-white/55">/100</span>
          </div>
        </div>
        <p className="max-w-[16rem] text-right text-sm leading-snug text-white/55">
          {result.verdict}
        </p>
      </motion.div>

      <motion.div {...stagger(1)}>
        <ScoreLabel>
          Complexity traps: {result.buzzwords.length} found ({result.buzzwordDensity}/100 words)
        </ScoreLabel>
        {result.buzzwords.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {result.buzzwords.map((w) => (
              <span
                key={w}
                className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[0.8rem] text-white/55 line-through decoration-white/30"
                style={{ fontFamily: "var(--proto-mono, ui-monospace, monospace)" }}
              >
                {w}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/55">None found. Your copy avoids the usual jargon traps.</p>
        )}
      </motion.div>

      <motion.div {...stagger(2)}>
        {/* v4：Translation Gap → 折價語彙 */}
        <ScoreLabel>Where the market discounts you</ScoreLabel>
        <div className="flex items-center gap-3 text-lg">
          <span className="text-white/45">{result.gap.from}</span>
          <span style={{ color: accent }}>→</span>
          <span className="font-medium text-white">{result.gap.to}</span>
        </div>
        <p className="mt-2 max-w-prose text-[0.95rem] leading-relaxed text-white/70">
          {result.tension}
        </p>
      </motion.div>

      <motion.div {...stagger(3)} className="rounded-xl border border-white/10 bg-black/30 p-4">
        <ScoreLabel>A sharper opening, drafted from your words</ScoreLabel>
        <p className="text-sm text-white/55 line-through decoration-white/20">{result.before}</p>
        <p className="mt-2 text-lg font-medium leading-snug text-white" style={{ textWrap: "balance" }}>
          {result.after}
        </p>
      </motion.div>

      <motion.div
        {...stagger(4)}
        className="flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between"
      >
        <p className="max-w-sm text-sm leading-relaxed text-white/50">{diagnostic.outro}</p>
        <a
          href="#contact"
          className="inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-black transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: accent }}
        >
          {diagnostic.outroCta}
          <span aria-hidden>→</span>
        </a>
      </motion.div>
    </motion.div>
  )
}

function ScoreLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mb-2 text-[0.7rem] uppercase tracking-[0.18em] text-white/55"
      style={{ fontFamily: "var(--proto-mono, ui-monospace, monospace)" }}
    >
      {children}
    </p>
  )
}

function CountUp({ to, reduce, accent }: { to: number; reduce: boolean; accent: string }) {
  const [n, setN] = useState(reduce ? to : 0)
  useEffect(() => {
    if (reduce) {
      setN(to)
      return
    }
    let raf = 0
    const start = performance.now()
    const dur = 900
    const tick = (now: number) => {
      const t = Math.min((now - start) / dur, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setN(Math.round(eased * to))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [to, reduce])
  return (
    <span className="text-6xl font-semibold tabular-nums tracking-tight" style={{ color: accent }}>
      {n}
    </span>
  )
}

function Spinner() {
  return (
    <motion.span
      className="block size-3 rounded-full border-2 border-current border-t-transparent"
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 0.7, ease: "linear" }}
    />
  )
}

function Dots() {
  return (
    <motion.span
      aria-hidden
      animate={{ opacity: [0.2, 1, 0.2] }}
      transition={{ repeat: Infinity, duration: 1.2 }}
    >
      …
    </motion.span>
  )
}
